import { DoneAll, Edit, RotateLeft } from '@mui/icons-material';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';

import {
  LinearProgressMainLayoutActivate,
  LinearProgressMainLayoutDeactivate,
} from '#app/Action/LinearProgressMainLayoutAction';
import { SnackbarOpen } from '#app/Action/SnackbarAction';
import { TableReload } from '#app/Action/TableAction';
import { dispatch } from '#app/App';
import { hasRight } from '#app/Common/UserCommon';
import ButtonComponent from '#app/Component/ButtonComponent';
import ContentViewComponent from '#app/Component/ContentViewComponent';
import IconAction from '#app/Component/IconAction';
import TableComponent from '#app/Component/TableComponent';
import TextFieldComponent from '#app/Component/TextFieldComponent';
import ApiHandler from '#app/Handler/ApiHandler';
import { initFormHandler } from '#app/Handler/FormHandler';

function SettingGalenicList() {
  const authenticationReducer = useSelector((state) => state.AuthenticationReducer);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [galenic, setGalenic] = React.useState(null);
  const [isUpdateSylius, setIsUpdateSylius] = React.useState(true);
  const [isLoadingSylius, setIsLoadingSylius] = React.useState(true);

  // Sylius
  const updateSylius = () => {
    setIsLoadingSylius(true);
    ApiHandler.get(
      {
        route: 'api_setting_galenic_sylius',
      },
      (response) => {
        if (response.status === 200) {
          setIsUpdateSylius(response.data.isUpdate);
        } else {
          dispatch(
            SnackbarOpen({
              text: response?.error?.message || "Une erreur inattendue s'est produite.",
              variant: 'error',
            }),
          );
          setIsUpdateSylius(false);
        }

        setIsLoadingSylius(false);
      },
    );
  };

  // Global
  const columns = [
    {
      name: 'id',
      label: 'Référence',
      options: { filter: true, sort: true },
    },
    {
      name: 'label',
      label: 'Libellé',
      options: { filter: true, sort: true },
    },
    {
      name: 'code',
      label: 'Code',
      options: { filter: true, sort: true },
    },
    {
      name: 'action',
      label: 'Action',
      options: { filter: false, sort: false },
    },
  ];
  const getAction = (row) => {
    if (
      hasRight(authenticationReducer, 'ROLE_SETTING_GALENIC') ||
      hasRight(authenticationReducer, 'ROLE_SETTING_GALENIC_EDIT')
    ) {
      return (
        <IconAction
          title="Modifier"
          icon="edit"
          onClick={() => {
            handlerForm.reset();
            setGalenic(row.id);
            handlerForm.setValue('label', row.label);
            handlerForm.setValue('code', row.code);
            setOpenDialog(true);
          }}
        />
      );
    } else {
      return (
        <IconButton style={{ cursor: 'no-drop' }}>
          <Edit />
        </IconButton>
      );
    }
  };

  // Form
  const [form, setForm] = React.useState({
    label: {
      name: 'label',
      label: 'Libellé',
      textHelper: 'Saisissez le libellé de la galénique.',
      type: 'text',
      defaultValue: '',
      options: { validation: ['required'] },
    },
    code: {
      name: 'code',
      label: 'Code',
      textHelper: 'Saisissez le code de la galénique.',
      type: 'text',
      defaultValue: '',
      options: { validation: ['required'], equal: 1 },
    },
  });
  const [isLoadingForm, setIsLoadingForm] = React.useState(false);
  const handlerForm = initFormHandler(form, setForm);
  const save = () => {
    if (handlerForm.checkError() < 1) {
      handlerForm.setFormLoading(true);
      setIsLoadingForm(true);
      dispatch(LinearProgressMainLayoutActivate());

      if (galenic) {
        ApiHandler.post(
          {
            route: 'api_setting_galenic_edit',
            data: handlerForm.getData(),
            params: { id: galenic },
          },
          (response) => {
            if (response.status === 200) {
              setOpenDialog(false);
              dispatch(TableReload('api_setting_galenic_list'));
              setGalenic(null);
              dispatch(
                SnackbarOpen({
                  text: 'Galénique modifiée avec succès.',
                  variant: 'success',
                }),
              );

              ApiHandler.clearCache('api_select_galenic');
            } else if (response.status === 400) {
              handlerForm.setErrorApi(response.error);
            } else {
              dispatch(
                SnackbarOpen({
                  text: response?.error?.message || "Une erreur inattendue s'est produite.",
                  variant: 'error',
                }),
              );
            }

            dispatch(LinearProgressMainLayoutDeactivate());
            handlerForm.setFormLoading(false);
            setIsLoadingForm(false);
          },
        );
      } else {
        ApiHandler.post(
          {
            route: 'api_setting_galenic_add',
            data: handlerForm.getData(),
          },
          (response) => {
            if (response.status === 200) {
              setOpenDialog(false);
              dispatch(TableReload('api_setting_galenic_list'));
              dispatch(
                SnackbarOpen({
                  text: 'Galénique créée avec succès.',
                  variant: 'success',
                }),
              );

              ApiHandler.clearCache('api_select_galenic');
            } else if (response.status === 400) {
              handlerForm.setErrorApi(response.error);
            } else {
              dispatch(
                SnackbarOpen({
                  text: response?.error?.message || "Une erreur inattendue s'est produite.",
                  variant: 'error',
                }),
              );
            }

            dispatch(LinearProgressMainLayoutDeactivate());
            handlerForm.setFormLoading(false);
            setIsLoadingForm(false);
          },
        );
      }
    }
  };

  React.useEffect(() => {
    updateSylius();
    handlerForm.start();
  }, []);

  return (
    <ContentViewComponent breadcrumbs={{ title: 'Liste des galéniques', context: 'Paramétrage' }}>
      <TableComponent
        id={'api_setting_galenic_list'}
        title={'Galéniques'}
        columns={columns}
        actionFirst={
          hasRight(authenticationReducer, 'ROLE_SETTING_GALENIC') ||
          hasRight(authenticationReducer, 'ROLE_SETTING_GALENIC_ADD')
            ? {
                label: 'Ajouter une galénique',
                onClick: () => {
                  setGalenic(null);
                  handlerForm.reset();
                  setOpenDialog(true);
                },
              }
            : false
        }
        actionSecond={
          hasRight(authenticationReducer, 'ROLE_SETTING_GALENIC') ||
          hasRight(authenticationReducer, 'ROLE_SETTING_GALENIC_LIST')
            ? {
                label: isUpdateSylius ? (
                  <span>
                    Sylius est à jour <DoneAll style={{ verticalAlign: 'top' }} />
                  </span>
                ) : (
                  <span>
                    Mettre à jour Sylius <RotateLeft style={{ verticalAlign: 'top' }} />
                  </span>
                ),
                color: isUpdateSylius ? '#006500' : '#ffc107',
                loading: isLoadingSylius,
                outlined: true,
                onClick: isUpdateSylius ? () => {} : updateSylius,
              }
            : false
        }
        promiseData={(resolve) => {
          ApiHandler.get({ route: 'api_setting_galenic_list' }, (response) => {
            const data = response.data;
            for (const index in data) {
              data[index].action = getAction(data[index]);
            }
            resolve(data);
          });
        }}
      />

      <Dialog open={openDialog} maxWidth={'xl'} onClose={() => setOpenDialog(false)}>
        <DialogTitle style={{ fontSize: 15 }}>
          {galenic ? 'Modifier une galénique' : 'Ajouter une galénique'}
        </DialogTitle>
        <DialogContent style={{ minWidth: '30vw' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextFieldComponent id={'label'} handler={handlerForm} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldComponent id={'code'} handler={handlerForm} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <ButtonComponent
            color={'#5E6E82'}
            label={'Annuler'}
            onClick={() => setOpenDialog(false)}
            disabled={isLoadingForm}
          />
          <ButtonComponent label={'Enregistrer'} onClick={save} loading={isLoadingForm} />
        </DialogActions>
      </Dialog>
    </ContentViewComponent>
  );
}

export default SettingGalenicList;
